import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDevice } from '@/contexts/DeviceContext';
import { useAvailableAppsContext } from '@/contexts/AvailableAppsContext';
import { useBlockedApps } from '@/hooks/useBlockedApps';
import { useMentorStatus } from '@/hooks/useMentorStatus';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

import AppCard from '@/components/AppCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, RefreshCw, Store, ShieldX, Apple, Loader2 } from 'lucide-react';
import { ProfileAvatar } from '@/components/ProfileAvatar';

import { App } from '@/types/App';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import PullToRefreshIndicator from '@/components/PullToRefreshIndicator';

const Index: React.FC = () => {
  const navigate = useNavigate();
  const { selectedDevice } = useDevice();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isSelfManaged } = useMentorStatus();
  
  // Use the shared context for app data
  const { 
    whitelistedApps,
    categoryApps,
    allApps,
    searchResults,
    availableCategories,
    isLoading,
    isFullyLoaded,
    isSearching,
    searchQuery,
    setSearchQuery,
    isSearchMode,
    error, 
    retry,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    debug: availableAppsDebug
  } = useAvailableAppsContext();
  
  const { blockedApps, hasAppleAppStore, isLoading: isLoadingBlocked, isLoadingConfig } = useBlockedApps();

  // Dynamic category options - sorted alphabetically from context
  const categoryOptions = useMemo(() => {
    const sorted = [...availableCategories].sort((a, b) => 
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
    return [
      { id: 'all', label: 'All Categories' },
      ...sorted.map(cat => ({ id: cat, label: cat }))
    ];
  }, [availableCategories]);

  // Infinite scroll hook - triggers loading when user scrolls to bottom
  const { loadMoreRef } = useInfiniteScroll({
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
    root: scrollContainerRef.current,
  });

  // Pull to refresh
  const { isPulling, isRefreshing, pullProgress, setContainerRef } = usePullToRefresh({
    onRefresh: async () => {
      retry();
    }
  });

  // Set ref for pull to refresh
  const setRef = useCallback((el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    setContainerRef(el);
  }, [setContainerRef]);

  // Helper function to match app category against selected category
  const matchesCategory = (appCategory: string | undefined, selectedCategoryId: string): boolean => {
    if (!appCategory) return false;
    if (selectedCategoryId === 'all') return true;
    
    const appCat = appCategory.toLowerCase().trim();
    const selectedCat = selectedCategoryId.toLowerCase().trim();
    
    // Exact match (case-insensitive)
    if (appCat === selectedCat) return true;
    
    // Handle variations
    const categoryMappings: Record<string, string[]> = {
      'photo & video': ['photo', 'video', 'photography', 'photo-video', 'camera', 'photos & video'],
      'productivity': ['productivity', 'business', 'work'],
      'health & fitness': ['health', 'fitness', 'health-fitness', 'healthcare', 'medical'],
      'social networking': ['social', 'social networking', 'social-networking', 'communication'],
      'utilities': ['utilities', 'utility', 'tools'],
      'lifestyle': ['lifestyle', 'life'],
      'music': ['music', 'audio'],
      'navigation': ['navigation', 'maps', 'travel'],
      'shopping': ['shopping', 'retail', 'ecommerce'],
      'games': ['games', 'game', 'gaming'],
      'education': ['education', 'educational', 'learning'],
      'finance': ['finance', 'financial', 'banking', 'money'],
      'entertainment': ['entertainment', 'media', 'streaming'],
    };
    
    const variations = categoryMappings[selectedCat];
    if (variations) {
      return variations.some(v => appCat.includes(v) || v.includes(appCat));
    }
    
    // Fallback: check if either contains the other
    return appCat.includes(selectedCat) || selectedCat.includes(appCat);
  };

  // Filter apps based on search and category (only for browse mode)
  const filteredWhitelisted = useMemo(() => {
    if (isSearchMode) return []; // Search results are flat, no sections
    
    let apps = whitelistedApps || [];
    
    if (selectedCategory !== 'all') {
      apps = apps.filter((app: App) => matchesCategory(app.category, selectedCategory));
    }
    
    return apps;
  }, [whitelistedApps, selectedCategory, isSearchMode]);

  const filteredCategory = useMemo(() => {
    if (isSearchMode) return []; // Search results are flat, no sections
    
    let apps = categoryApps || [];
    
    if (selectedCategory !== 'all') {
      apps = apps.filter((app: App) => matchesCategory(app.category, selectedCategory));
    }
    
    return apps;
  }, [categoryApps, selectedCategory, isSearchMode]);

  // Display apps - either search results or filtered browse
  const displayApps = useMemo(() => {
    if (isSearchMode) {
      return searchResults;
    }
    return [...filteredWhitelisted, ...filteredCategory];
  }, [isSearchMode, searchResults, filteredWhitelisted, filteredCategory]);

  // Filter blocked apps based on search query (for Apple App Store mode)
  const filteredBlockedApps = useMemo(() => {
    if (!searchQuery.trim()) return blockedApps;
    const query = searchQuery.toLowerCase();
    return blockedApps.filter(app => 
      app.name.toLowerCase().includes(query)
    );
  }, [blockedApps, searchQuery]);

  const handleAppSelect = (app: App) => {
    if (selectedDevice?._id) {
      navigate(`/${selectedDevice._id}/app/${app.appStoreId}`);
    }
  };

  const handleNavigateToUpdates = () => {
    if (selectedDevice?._id) {
      navigate(`/${selectedDevice._id}/updates`);
    } else {
      navigate('/updates');
    }
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
  };

  // Skeleton loading component for app list
  const LoadingSkeleton = () => (
    <div className="divide-y divide-border/50" role="status" aria-live="polite" aria-label="Loading apps">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="px-4 py-3 flex items-center gap-4">
          <Skeleton className="w-14 h-14 rounded-xl flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="w-5 h-5 rounded" />
        </div>
      ))}
      <span className="sr-only">Loading apps...</span>
    </div>
  );

  // Show loading state while determining app store mode to prevent flickering
  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-28">
        <header className="sticky top-0 z-20 bg-background/95 backdrop-blur-lg border-b border-border/50 safe-area-top">
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-center gap-3 mb-3">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <Skeleton className="h-7 w-32" />
            </div>
            <Skeleton className="h-11 w-full rounded-xl" />
          </div>
        </header>
        <main className="px-4 pt-4">
          <LoadingSkeleton />
        </main>
      </div>
    );
  }

  // Only show error for non-Apple App Store mode
  if (error && !hasAppleAppStore) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="text-center space-y-3">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-destructive/10 flex items-center justify-center">
            <span className="text-destructive text-2xl font-bold">!</span>
          </div>
          <p className="text-foreground font-semibold text-lg">Error loading apps</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
          <Button onClick={retry} variant="outline" className="mt-4">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Apple App Store Mode - show blocked apps
  if (hasAppleAppStore) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-28">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)] safe-area-top">
          <div className="px-5 pt-5 pb-4">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center shadow-md">
                  <Apple className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-2xl font-bold tracking-tight text-foreground">Apple App Store</h1>
              </div>
              <ProfileAvatar />
            </div>
            
            {/* Info Banner */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Apple className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Your device uses the Apple App Store</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    You can download any app from the App Store, except for the restricted apps listed below.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Search Bar */}
            {blockedApps.length > 0 && (
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" aria-hidden="true" />
                <Input
                  type="search"
                  placeholder="Search blocked apps..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-11 bg-muted/50 border-0 rounded-xl placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary/30 text-[15px]"
                  aria-label="Search blocked apps"
                />
              </div>
            )}
          </div>
        </header>

        {/* Blocked Apps Content */}
        <main 
          ref={setRef}
          className="relative overflow-auto no-overscroll"
          style={{ 
            height: 'calc(100dvh - 280px)',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <PullToRefreshIndicator 
            isPulling={isPulling} 
            isRefreshing={isRefreshing} 
            pullProgress={pullProgress} 
          />
          
          {isLoadingBlocked ? (
            <LoadingSkeleton />
          ) : filteredBlockedApps.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mb-4">
                <Apple className="w-8 h-8 text-green-600" aria-hidden="true" />
              </div>
              <p className="text-foreground font-semibold text-lg">
                {searchQuery ? 'No blocked apps match your search' : 'No apps are restricted'}
              </p>
              <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                {searchQuery ? 'Try a different search term' : 'You have full access to all apps on the Apple App Store.'}
              </p>
            </div>
          ) : (
            <div className="px-4 py-2">
              {/* Section Header */}
              <div className="flex items-center gap-2 mb-3">
                <ShieldX className="w-4 h-4 text-destructive" />
                <p className="text-sm font-medium text-muted-foreground">
                  Restricted Apps ({filteredBlockedApps.length})
                </p>
              </div>
              
              {/* Blocked Apps List */}
              <div className="bg-card rounded-xl border border-border/50 divide-y divide-border/50 overflow-hidden">
                {filteredBlockedApps.map((app) => (
                  <div 
                    key={app.id} 
                    className="px-4 py-3 flex items-center gap-3"
                  >
                    {/* App Icon */}
                    <div className="relative flex-shrink-0">
                      <img 
                        src={app.icon} 
                        alt={`${app.name} icon`}
                        className="w-12 h-12 rounded-xl object-cover bg-muted"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder.svg';
                        }}
                      />
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-destructive flex items-center justify-center">
                        <ShieldX className="w-3 h-3 text-destructive-foreground" />
                      </div>
                    </div>
                    
                    {/* App Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{app.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        Source: {app.source}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // Determine if we're still loading the full browse list
  const isLoadingBrowse = !isSearchMode && !isFullyLoaded && isLoading;
  const isLoadingMore = isFetchingNextPage || hasNextPage;

  // Regular App Store Mode
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-28">
      {import.meta.env.DEV && (
        <div className="fixed left-3 bottom-24 z-50 rounded-lg border border-border bg-background/95 backdrop-blur px-3 py-2 text-xs text-foreground shadow-sm">
          <div className="font-medium">Apps debug</div>
          <div className="text-muted-foreground">
            total: {displayApps.length} • isSearchMode: {String(isSearchMode)}
          </div>
          <div className="text-muted-foreground">
            status: {String(availableAppsDebug?.status ?? 'n/a')} • serial: {String(availableAppsDebug?.serial ?? 'n/a')}
          </div>
          <div className="text-muted-foreground">
            isFullyLoaded: {String(isFullyLoaded)} • loadingMore: {String(isLoadingMore)}
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)] safe-area-top">
        <div className="px-5 pt-5 pb-4">
          {/* Title Row */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                <Store className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">App Store</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-primary font-semibold hover:bg-primary/10 active:bg-primary/20 active:scale-[0.97] gap-1.5 transition-all shrink-0"
                onClick={handleNavigateToUpdates}
                aria-label="View app updates"
              >
                <RefreshCw className="w-4 h-4" />
                Updates
              </Button>
              <ProfileAvatar />
            </div>
          </div>
          
          <p className="text-[13px] text-muted-foreground mb-4">
            {isSelfManaged ? (
              <>
                To add an app,{' '}
                <Link 
                  to={`/${selectedDevice?._id}/settings/app-management/whitelist?add=true`}
                  className="text-primary font-medium hover:underline"
                >
                  press here
                </Link>
              </>
            ) : (
              'To add an app, request it in the Requests section'
            )}
          </p>
          
          {/* Search Bar - Always visible */}
          <div className="relative mb-3">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" aria-hidden="true" />
            <Input
              type="search"
              placeholder="Search apps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 bg-muted/50 border-0 rounded-xl placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary/30 text-[15px]"
              aria-label="Search apps"
            />
            {isSearching && (
              <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          
          {/* Category Dropdown - Hidden during search */}
          {!isSearchMode && (
            <Select value={selectedCategory} onValueChange={handleCategoryChange}>
              <SelectTrigger className="h-11 bg-muted/50 border-0 rounded-xl focus:ring-1 focus:ring-primary/30">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </header>

      {/* Content */}
      <main 
        ref={setRef}
        className="relative overflow-auto no-overscroll pb-20"
        style={{ 
          height: 'calc(100dvh - 140px)',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <PullToRefreshIndicator 
          isPulling={isPulling} 
          isRefreshing={isRefreshing} 
          pullProgress={pullProgress} 
        />
        
        {/* Loading State */}
        {(isLoading || isLoadingBrowse) ? (
          <div className="px-4 py-4">
            <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading apps...</span>
            </div>
            <LoadingSkeleton />
          </div>
        ) : displayApps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <Search className="w-7 h-7 text-muted-foreground" aria-hidden="true" />
            </div>
            <p className="text-foreground font-semibold text-lg">
              {isSearchMode ? 'No apps found' : 'No apps available'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {isSearchMode ? 'Try a different search term' : 'No apps match the selected category'}
            </p>
          </div>
        ) : (
          <>
            {/* Status Info */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-border/30">
              <p className="text-xs text-muted-foreground">
                {isSearchMode 
                  ? `${displayApps.length} results for "${searchQuery}"`
                  : `${displayApps.length} apps${selectedCategory !== 'all' ? ` in ${selectedCategory}` : ''}`
                }
              </p>
              {isLoadingMore && !isSearchMode && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Loading more...
                </div>
              )}
            </div>

            {/* Search Results - Flat list, no sections */}
            {isSearchMode && (
              <div className="divide-y divide-border/50">
                {displayApps.map((app: App) => (
                  <AppCard 
                    key={app.id} 
                    app={app} 
                    onAppSelect={handleAppSelect}
                  />
                ))}
              </div>
            )}

            {/* Browse Mode - Whitelisted + Category sections */}
            {!isSearchMode && (
              <>
                {/* Whitelisted Apps Section */}
                {filteredWhitelisted.length > 0 && (
                  <>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground px-4 pt-4 pb-2">
                      Whitelisted
                    </p>
                    <div className="divide-y divide-border/50">
                      {filteredWhitelisted.map((app: App) => (
                        <AppCard 
                          key={app.id} 
                          app={app} 
                          onAppSelect={handleAppSelect}
                        />
                      ))}
                    </div>
                  </>
                )}

                {/* All Other Apps Section - now visible to all users */}
                {filteredCategory.length > 0 && (
                  <>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground px-4 pt-4 pb-2">
                      All Other
                    </p>
                    <div className="divide-y divide-border/50">
                      {filteredCategory.map((app: App) => (
                        <AppCard 
                          key={app.id} 
                          app={app} 
                          onAppSelect={handleAppSelect}
                        />
                      ))}
                    </div>
                  </>
                )}

                {/* Infinite scroll sentinel - triggers loading more apps */}
                {hasNextPage && (
                  <div ref={loadMoreRef} className="py-6 flex justify-center">
                    {isFetchingNextPage && (
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Index;
